import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { CerebroTextButton } from '../../shared/components';
import { useApi } from '../../shared/context';
import { QueryKeys } from '../../shared/hooks';
import { UnlockUser, CurrentUser } from '../../shared/models';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getResponseData } from '../../shared/utils/axios';

interface UnlockUserDialogProps {
	dataRoomGuid: string | undefined;
	user: UnlockUser | null;
	close: () => void;
}

export const UnlockUserDialog: React.FC<UnlockUserDialogProps> = ({ dataRoomGuid, user, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const onUnlockConfirmation = () => {
		if (user !== null) unlockUser(user);
	};

	const { mutate: unlockUser } = useMutation<CurrentUser, unknown, UnlockUser>(
		userToUnlock => getResponseData(api.post<CurrentUser>(CerebroApiEndpoints.Users.Unlock(userToUnlock.userGuid))),
		{
			onMutate: () => {
				close();
			},
			onSuccess: () => {
				switch (user?.roleType) {
					case 'BankUser':
					case 'DataRoomUser':
						client.invalidateQueries(QueryKeys.DataRoomUsers.Root(dataRoomGuid));
						break;
					case 'CompanyUser':
						break;
					case 'DealPartnerUser':
						break;
					case 'TeamMemberUser':
						break;
				}
				client.invalidateQueries(QueryKeys.Milestones.Root(dataRoomGuid));
				client.invalidateQueries(QueryKeys.DataRoomUsers.TeamMembersByRoom(dataRoomGuid));

				snackbar.enqueueSnackbar('User account successfully is unlocked.', { variant: 'success' });
				close();
			},
			onError: () => {
				client.invalidateQueries(QueryKeys.Teasers.Root(dataRoomGuid));
				snackbar.enqueueSnackbar('There was a problem unlocking the user account.', { variant: 'error' });
			},
		}
	);

	return (
		<Dialog open={user !== null} onClose={close}>
			<DialogTitle>Confirm Unlock User</DialogTitle>
			<DialogContent>
				<DialogContentText>
					Are you sure you want to unlock user account for {user?.firstName} {user?.lastName} at{' '}
					{user?.organization}?
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={close}>No</CerebroTextButton>
				<CerebroTextButton onClick={onUnlockConfirmation}>Yes</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};
